/**
 * JiraPreviewPanelsContext — the React context for the Jira Preview Panels system.
 *
 * Kept in its own dedicated file so that both the lightweight hook file
 */
import type React from 'react';
import { createContext, type MutableRefObject } from 'react';
import type { Panel } from '@atlassian/preview-panels-api/types';

type OpenPreviewPanel = (props: {
	url: string;
	ari: string;
	name: string;
	iconUrl?: string;
	parentProduct?: string;
}) => void;
type IsPreviewPanelAvailable = (props: { ari: string }) => boolean;
type IsPreviewRestricted = (props: { ari: string }) => boolean;
type Action = 'open' | 'close';
export type PreviewPanelEvent = {
	action: Action;
	panel: Panel;
};

export type IssuePreviewPanelsNavigationRef = {
	onNavigateAway: MutableRefObject<((toPanel: Panel) => void) | undefined>;
	onNavigateNext: MutableRefObject<(() => void) | undefined>;
	onNavigatePrevious: MutableRefObject<(() => void) | undefined>;
};

export type JiraPreviewPanelsContextValue = {
	subscribe: (callback: (event: PreviewPanelEvent) => void) => () => void;
	openPreviewPanel: OpenPreviewPanel;
	/** Refs are used to hydrate the navigation functions on the client side by consumers, without causing SSRed content to change */
	navigation?: IssuePreviewPanelsNavigationRef;
	isPreviewPanelAvailable: IsPreviewPanelAvailable;
	/** When true (cross-unit), smart cards suppress the preview panel and modal. */
	isPreviewRestricted: IsPreviewRestricted;
	closePreviewPanel: () => void;
} | null;

export const JiraPreviewPanelsContext: React.Context<JiraPreviewPanelsContextValue> =
	createContext<JiraPreviewPanelsContextValue>(null);
